"use client";

import { AnimatePresence, m } from "motion/react";
import { cn } from "@marketing-sites/utils/cn";
import { getAnalyticsRegion } from "@marketing-sites/analytics/get-analytics-region";
import { triggerAnalyticsClick } from "@marketing-sites/analytics/trigger-analytics-event";
import { TrackingType } from "@marketing-sites/analytics/types";
import { type Chapter } from "./chapter-progress.lib";
import { type RevealRatio, useChapterProgress } from "./chapter-progress.hook";

const TRANSITION = { duration: 0.25, ease: "easeOut" as const };
const SWAP_TRANSITION = { duration: 0.18 };

export interface ChapterProgressProps {
  label: string;
  className?: string;
  sectionMetadata: { analyticsId: string };
  /** Ids for the nav's own toggle; open and close stay distinct events. */
  navOpenMetadata: { analyticsId: string };
  navCloseMetadata: { analyticsId: string };
  /** Detection line as a fraction of viewport height, for pages whose chapters are taller than the screen. Defaults to a fixed line near the top. */
  detectionRatio?: number;
  /** Viewport fraction the first chapter's top must rise past before the bar appears. Pass `{ bar, floating }` to split it between the below-xl top bar and the xl+ floating panel. Defaults to appearing with the first active chapter. */
  revealRatio?: RevealRatio;
}

const ChevronIcon = () => (
  <svg width="8" height="5" viewBox="0 0 8 5" fill="none">
    <path d="M0 0L4 5L8 0H0Z" fill="currentColor" />
  </svg>
);

function Trigger({
  label,
  showLabel,
  expanded,
  activeIdx,
  activeName,
  onToggle,
  navOpenMetadata,
  navCloseMetadata,
}: {
  label: string;
  showLabel: boolean;
  expanded: boolean;
  activeIdx: number | null;
  activeName: string | undefined;
  onToggle: () => void;
  navOpenMetadata: { analyticsId: string };
  navCloseMetadata: { analyticsId: string };
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        const opening = !expanded;
        triggerAnalyticsClick({
          text: `Chapter navigation — ${opening ? "Open" : "Close"}`,
          analyticsId: opening
            ? navOpenMetadata.analyticsId
            : navCloseMetadata.analyticsId,
          location: getAnalyticsRegion(event.currentTarget) ?? "Unknown",
          type: TrackingType.Button,
          metadata: { state: opening ? "open" : "close" },
        });
        onToggle();
      }}
      aria-expanded={expanded}
      aria-haspopup="listbox"
      aria-label="Toggle chapter navigation"
      className="flex w-full cursor-pointer items-center justify-between px-4 py-3.5 text-left"
    >
      <div className="text-body min-w-0 flex-1">
        {/* Fade between labels as the active chapter changes — keyed per
            chapter so each crossing animates. `mode="wait"` can't animate
            faster than the swap duration, so a fast scroll (manual or a
            click-driven jump) coarsens into a few fades that land on the
            destination rather than flicking through every chapter. */}
        <AnimatePresence mode="wait">
          <m.span
            key={showLabel ? "label" : `chapter-${activeIdx}`}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={SWAP_TRANSITION}
            className="block truncate"
          >
            {showLabel || activeIdx === null ? (
              label
            ) : (
              <>
                {String(activeIdx + 1).padStart(2, "0")}
                {activeName && <span className="ml-5">{activeName}</span>}
              </>
            )}
          </m.span>
        </AnimatePresence>
      </div>
      <m.span
        animate={{ rotate: expanded ? 180 : 0 }}
        transition={TRANSITION}
        className="ml-2 inline-flex shrink-0"
      >
        <ChevronIcon />
      </m.span>
    </button>
  );
}

function ChapterListItem({
  chapter,
  index,
  isActive,
  onSelect,
}: {
  chapter: Chapter;
  index: number;
  isActive: boolean;
  onSelect: (idx: number) => void;
}) {
  return (
    // Presentational li so the option is the listbox's effective child; the
    // button carries the option role + selected state.
    <li role="presentation">
      <button
        type="button"
        role="option"
        onClick={(event) => {
          triggerAnalyticsClick({
            text: chapter.name ?? `Chapter ${index + 1}`,
            analyticsId: chapter.analyticsId,
            location: getAnalyticsRegion(event.currentTarget) ?? "Unknown",
            type: TrackingType.NavLink,
            metadata: { chapterIndex: index + 1, chapter: chapter.name },
          });
          onSelect(index);
        }}
        aria-selected={isActive}
        className={cn(
          "text-body! flex w-full cursor-pointer items-center gap-5 px-4 py-2 text-left transition-colors",
          isActive
            ? "bg-chalk text-current"
            : "text-current/50 hover:bg-chalk/50 hover:text-current",
        )}
      >
        <span className="shrink-0 tabular-nums">
          {String(index + 1).padStart(2, "0")}
        </span>
        {chapter.name && <span className="truncate">{chapter.name}</span>}
      </button>
    </li>
  );
}

function ChapterPanel({
  expanded,
  chapters,
  activeIdx,
  onSelect,
}: {
  expanded: boolean;
  chapters: Chapter[];
  activeIdx: number | null;
  onSelect: (idx: number) => void;
}) {
  return (
    <AnimatePresence>
      {expanded && (
        <m.div
          key="panel"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={TRANSITION}
          style={{ overflow: "hidden" }}
        >
          <ul
            role="listbox"
            aria-label="Chapters"
            className="border-current/10 max-h-[calc(100dvh-260px)] overflow-y-auto border-t xl:max-h-[280px]"
          >
            {chapters.map((chapter, i) => (
              <ChapterListItem
                key={i}
                chapter={chapter}
                index={i}
                isActive={i === activeIdx}
                onSelect={onSelect}
              />
            ))}
          </ul>
        </m.div>
      )}
    </AnimatePresence>
  );
}

export function ChapterProgress({
  label,
  className,
  sectionMetadata,
  navOpenMetadata,
  navCloseMetadata,
  detectionRatio,
  revealRatio,
}: ChapterProgressProps) {
  const {
    chapters,
    activeIdx,
    activeName,
    visible,
    expanded,
    toggleExpanded,
    showLabel,
    containerRef,
    scrollToChapter,
  } = useChapterProgress(detectionRatio, revealRatio);

  if (!chapters) return null;

  return (
    <AnimatePresence>
      {visible && (
        <m.div
          ref={containerRef}
          data-analytics-region="chapter-nav"
          data-analytics-id={sectionMetadata.analyticsId}
          className={cn(
            "border-current/10 bg-off-white fixed inset-x-0 top-[56px] z-40 border-b xl:inset-x-auto xl:bottom-[34px] xl:left-6 xl:top-auto xl:w-[260px] xl:border xl:bg-white max-xl:[body:has(.top-banner)_&]:top-[97px]",
            className,
          )}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={TRANSITION}
        >
          <Trigger
            label={label}
            showLabel={showLabel}
            expanded={expanded}
            activeIdx={activeIdx}
            activeName={activeName}
            onToggle={toggleExpanded}
            navOpenMetadata={navOpenMetadata}
            navCloseMetadata={navCloseMetadata}
          />
          <ChapterPanel
            expanded={expanded}
            chapters={chapters}
            activeIdx={activeIdx}
            onSelect={scrollToChapter}
          />
        </m.div>
      )}
    </AnimatePresence>
  );
}

export default ChapterProgress;
